import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getGatewayBaseUrl } from "../config";
import {
  PosCatalogPriceGatewayClient,
  type CatalogModifierGroupProjection,
  type CatalogModifierOptionProjection,
  type CatalogVariantProjection,
  type ModifierPriceSnapshot,
  type PriceSnapshot,
} from "../gateway/catalog-price-client";
import type { CommerceCalculationRuntimeItem } from "../gateway/local-projection-client";
import {
  applyCatalogRuntimePayload,
  applyModifierPriceRuntimePayload,
  applyPriceRuntimePayload,
  readCatalogPriceRuntime,
  type LocalCatalogMenu,
  type LocalCatalogPriceRuntime,
} from "../local-store/catalog-price-runtime";
import { applyLocalCartLineConfigurations } from "../local-store/cart-edit-configurations";
import {
  applyLocalCartLine,
  applyLocalCartLineConfiguration,
  applyLocalCartLineQuantity,
  readLocalCart,
  saveLocalCart,
  type LocalCartDraft,
} from "../local-store/cart-state";
import { readCommerceCalculationRuntime } from "../local-store/commerce-calculation-runtime";
import {
  captureCurrentCartRuntimeToken,
  isCurrentCartRuntimeTokenCurrent,
  publishCurrentCartRuntime,
  publishCurrentCartRuntimeIfCurrent,
} from "../local-store/current-cart-runtime";
import { readLocalEmployeeSession } from "../local-store/employee-session";
import type { TerminalCredentialState } from "../local-store/terminal-state";
import { useRuntimeAutoSync } from "../sync/use-runtime-auto-sync";
import { variantDisplayLabel } from "./variant-presentation";

export type CatalogRuntimeStatus = "loading" | "local" | "syncing" | "ready" | "error";

export type SelectedModifier = {
  group: CatalogModifierGroupProjection;
  option: CatalogModifierOptionProjection;
  quantity: number;
  price: ModifierPriceSnapshot;
};

type PresentedLocalCatalogMenu = LocalCatalogMenu & {
  sourceMenuItemId?: string;
};

type SaleCardPresentationMode = "PRODUCT_CARD" | "SELLABLE_VARIANT_CARDS";

const RUNTIME_FULL_RECONCILE_INTERVAL_MS = 5 * 60 * 1000;

const emptyRuntime: LocalCatalogPriceRuntime = {
  catalogRevision: 0,
  priceCursor: null,
  modifierPriceCursor: null,
  menus: [],
  prices: [],
  modifierPrices: [],
};

function priceKey(menuItemId: string, menuVariantId: string | null) {
  return `${menuItemId}:${menuVariantId ?? "BASE"}`;
}

function validCurrencyScale(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 6;
}

function localPriceProjectionNeedsBootstrap(runtime: LocalCatalogPriceRuntime) {
  return runtime.prices.some((price) => price.snapshot.ready && !validCurrencyScale(price.snapshot.currency_scale));
}

function saleCardPresentationMode(menu: LocalCatalogMenu): SaleCardPresentationMode {
  const value = (menu.snapshot.menu as typeof menu.snapshot.menu & {
    sale_card_presentation_mode?: string | null;
  }).sale_card_presentation_mode;
  return value === "SELLABLE_VARIANT_CARDS" ? "SELLABLE_VARIANT_CARDS" : "PRODUCT_CARD";
}

function buildLocalCartLine(
  menu: PresentedLocalCatalogMenu,
  variant: CatalogVariantProjection,
  price: PriceSnapshot,
  selectedModifiers: SelectedModifier[],
) {
  const modifierTotal = selectedModifiers.reduce(
    (sum, modifier) => sum + modifier.price.price_delta * modifier.quantity,
    0,
  );

  return {
    menuItemId: menu.sourceMenuItemId ?? menu.menuItemId,
    menuVariantId: variant.variant_id,
    menuCategoryId: menu.snapshot.menu.menu_category_id ?? null,
    variantOptionIds: [...new Set((variant.options ?? []).map((option) => option.option_id))].sort(),
    menuName: menu.snapshot.menu.name,
    variantLabel: variantDisplayLabel(variant) ?? "",
    baseUnitPrice: price.final_price,
    unitPrice: price.final_price + modifierTotal,
    currencyCode: price.currency_code,
    currencyScale: validCurrencyScale(price.currency_scale) ? price.currency_scale : null,
    priceVersionId: price.price_version_id,
    modifiers: selectedModifiers.map((modifier) => ({
      modifierGroupId: modifier.group.modifier_group_id,
      modifierGroupName: modifier.group.name,
      modifierOptionId: modifier.option.modifier_option_id,
      modifierOptionName: modifier.option.name,
      quantity: modifier.quantity,
      priceDelta: modifier.price.price_delta,
      priceVersionId: modifier.price.price_version_id,
    })),
  };
}

async function reconcileCartCurrencyScale(
  identity: Parameters<typeof readCatalogPriceRuntime>[0],
  cart: LocalCartDraft,
  runtime: LocalCatalogPriceRuntime,
) {
  if (!cart.lines.some((line) => !validCurrencyScale(line.currencyScale))) return cart;

  const priceIndex = new Map(
    runtime.prices.map((price) => [priceKey(price.menuItemId, price.menuVariantId), price.snapshot] as const),
  );
  let changed = false;
  const lines = cart.lines.map((line) => {
    if (validCurrencyScale(line.currencyScale)) return line;
    const currentPrice = priceIndex.get(priceKey(line.menuItemId, line.menuVariantId));
    if (
      !currentPrice?.ready ||
      currentPrice.price_version_id !== line.priceVersionId ||
      currentPrice.currency_code !== line.currencyCode ||
      !validCurrencyScale(currentPrice.currency_scale)
    ) {
      return line;
    }
    changed = true;
    return { ...line, currencyScale: currentPrice.currency_scale };
  });

  if (!changed) return cart;
  return saveLocalCart(identity, { ...cart, lines, quote: null });
}

export function usePosCatalogPriceRuntime(
  terminal: TerminalCredentialState,
  online: boolean,
  commerceCalculation: CommerceCalculationRuntimeItem | null = null,
) {
  const identity = terminal.identity;
  const scopeKey = identity
    ? `${identity.organization_id}:${identity.branch_id}:${identity.commerce_channel_id}:${identity.node_id}`
    : "";
  const [runtime, setRuntime] = useState<LocalCatalogPriceRuntime>(emptyRuntime);
  const [cart, setCart] = useState<LocalCartDraft | null>(null);
  const [status, setStatus] = useState<CatalogRuntimeStatus>("loading");
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const lastFullReconcileAtRef = useRef(0);
  const lastFullReconcileScopeRef = useRef("");
  const cartRef = useRef<LocalCartDraft | null>(null);
  const cartWriteQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingCartWritesRef = useRef(0);

  const publishCart = useCallback((next: LocalCartDraft | null) => {
    cartRef.current = next;
    if (identity) publishCurrentCartRuntime(identity, next);
    setCart(next);
  }, [identity]);

  const publishDurableCartIfCurrent = useCallback((next: LocalCartDraft | null, token: ReturnType<typeof captureCurrentCartRuntimeToken>) => {
    if (!identity) return false;
    if (!publishCurrentCartRuntimeIfCurrent(identity, next, token)) return false;
    cartRef.current = next;
    setCart(next);
    return true;
  }, [identity]);

  const enqueueCartSave = useCallback((snapshot: LocalCartDraft) => {
    if (!identity) return;
    const target = snapshot;
    pendingCartWritesRef.current += 1;
    cartWriteQueueRef.current = cartWriteQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          const saved = await saveLocalCart(identity, target);
          if (cartRef.current === target) publishCart(saved);
        } catch {
          setRuntimeError("POS_CART_LOCAL_SAVE_FAILED");
          if (cartRef.current === target) {
            const recoveryToken = captureCurrentCartRuntimeToken(identity);
            const restored = await readLocalCart(identity);
            if (cartRef.current === target) publishDurableCartIfCurrent(restored, recoveryToken);
          }
        } finally {
          pendingCartWritesRef.current = Math.max(0, pendingCartWritesRef.current - 1);
        }
      });
  }, [identity, publishCart, publishDurableCartIfCurrent]);

  const priceIndex = useMemo(() => {
    const map = new Map<string, PriceSnapshot>();
    for (const price of runtime.prices) map.set(priceKey(price.menuItemId, price.menuVariantId), price.snapshot);
    return map;
  }, [runtime.prices]);

  const modifierPriceIndex = useMemo(() => {
    const map = new Map<string, ModifierPriceSnapshot>();
    for (const price of runtime.modifierPrices) map.set(price.modifierOptionId, price.snapshot);
    return map;
  }, [runtime.modifierPrices]);

  const sellableMenus = useMemo(() => {
    return runtime.menus
      .map((entry) => {
        const variants = (entry.snapshot.variants ?? [])
          .map((variant) => ({ variant, price: priceIndex.get(priceKey(entry.menuItemId, variant.variant_id)) ?? null }))
          .filter((entry): entry is { variant: CatalogVariantProjection; price: PriceSnapshot } => Boolean(entry.price?.ready));
        return { ...entry, sellableVariants: variants };
      })
      .filter((entry) => entry.sellableVariants.length > 0)
      .flatMap((entry) => {
        if (
          saleCardPresentationMode(entry) !== "SELLABLE_VARIANT_CARDS" ||
          entry.sellableVariants.length <= 1
        ) {
          return [entry];
        }

        const productCardName =
          entry.snapshot.menu.effective_card_display_name?.trim() ||
          entry.snapshot.menu.name;

        return entry.sellableVariants.map((choice) => {
          const variantLabel = variantDisplayLabel(choice.variant);
          return {
            ...entry,
            sourceMenuItemId: entry.menuItemId,
            menuItemId: `${entry.menuItemId}:${choice.variant.variant_id}`,
            snapshot: {
              ...entry.snapshot,
              menu: {
                ...entry.snapshot.menu,
                effective_card_display_name: variantLabel
                  ? `${productCardName} ${variantLabel}`
                  : productCardName,
              },
            },
            sellableVariants: [choice],
          };
        });
      })
      .sort((a, b) => {
        const categoryCompare = (a.snapshot.menu.category_name ?? "").localeCompare(b.snapshot.menu.category_name ?? "", "th");
        return categoryCompare !== 0 ? categoryCompare : (a.snapshot.menu.effective_card_display_name ?? a.snapshot.menu.name).localeCompare(b.snapshot.menu.effective_card_display_name ?? b.snapshot.menu.name, "th");
      });
  }, [priceIndex, runtime.menus]);

  const reloadLocal = useCallback(async () => {
    if (!identity) return;
    const readToken = captureCurrentCartRuntimeToken(identity);
    const [localRuntime, localCart] = await Promise.all([readCatalogPriceRuntime(identity), readLocalCart(identity)]);
    setRuntime(localRuntime);
    if (pendingCartWritesRef.current === 0) publishDurableCartIfCurrent(localCart, readToken);
    setStatus(localRuntime.menus.length || localRuntime.prices.length ? "local" : "loading");
  }, [identity, publishDurableCartIfCurrent]);

  const sync = useCallback(async () => {
    if (!identity || !online) return;
    const baseUrl = getGatewayBaseUrl();
    if (!baseUrl) {
      setRuntimeError("ยังไม่ได้กำหนด MORE POS Gateway");
      setStatus("error");
      return;
    }

    const employeeSession = await readLocalEmployeeSession();
    if (!employeeSession?.sessionToken) return;

    if (lastFullReconcileScopeRef.current !== scopeKey) {
      lastFullReconcileScopeRef.current = scopeKey;
      lastFullReconcileAtRef.current = 0;
    }

    const fullReconcile = Date.now() - lastFullReconcileAtRef.current >= RUNTIME_FULL_RECONCILE_INTERVAL_MS;

    setStatus("syncing");
    setRuntimeError(null);
    const client = new PosCatalogPriceGatewayClient({ baseUrl });
    let local = await readCatalogPriceRuntime(identity);

    // Delta is the normal fast path. A bounded background FULL pass on startup and
    // periodically repairs any local projection/cursor drift without putting Core
    // on the critical sale interaction path or requiring staff refresh/restart.
    let catalogRevision = fullReconcile ? 0 : local.catalogRevision;
    for (let page = 0; page < 20; page += 1) {
      const result = await client.catalog({ deviceSecret: terminal.deviceSecret, sessionToken: employeeSession.sessionToken, sinceRevision: catalogRevision, limit: 500 });
      if (!result.ok || !result.data) {
        setRuntimeError(result.error ?? "POS_CATALOG_RUNTIME_LOAD_FAILED");
        setStatus(local.menus.length ? "local" : "error");
        return;
      }
      catalogRevision = await applyCatalogRuntimePayload(identity, result.data);
      if (!result.data.has_more) break;
      if (page === 19) { setRuntimeError("CATALOG_RUNTIME_PAGE_GUARD_REACHED"); setStatus("error"); return; }
    }

    local = await readCatalogPriceRuntime(identity);
    let priceCursor = fullReconcile || localPriceProjectionNeedsBootstrap(local) ? null : local.priceCursor;
    for (let page = 0; page < 20; page += 1) {
      const result = await client.price({ deviceSecret: terminal.deviceSecret, sessionToken: employeeSession.sessionToken, cursor: priceCursor, limit: 1000 });
      if (!result.ok || !result.data) {
        const latest = await readCatalogPriceRuntime(identity);
        setRuntime(latest);
        setRuntimeError(result.error ?? "POS_PRICE_RUNTIME_LOAD_FAILED");
        setStatus(latest.menus.length ? "local" : "error");
        return;
      }
      priceCursor = await applyPriceRuntimePayload(identity, result.data);
      if (!result.data.has_more) break;
      if (page === 19) { setRuntimeError("PRICE_RUNTIME_PAGE_GUARD_REACHED"); setStatus("error"); return; }
    }

    local = await readCatalogPriceRuntime(identity);
    let modifierCursor = fullReconcile ? null : local.modifierPriceCursor;
    for (let page = 0; page < 20; page += 1) {
      const result = await client.modifierPrice({ deviceSecret: terminal.deviceSecret, sessionToken: employeeSession.sessionToken, cursor: modifierCursor, limit: 1000 });
      if (!result.ok || !result.data) {
        const latest = await readCatalogPriceRuntime(identity);
        setRuntime(latest);
        setRuntimeError(result.error ?? "POS_MODIFIER_PRICE_RUNTIME_LOAD_FAILED");
        setStatus(latest.menus.length ? "local" : "error");
        return;
      }
      modifierCursor = await applyModifierPriceRuntimePayload(identity, result.data);
      if (!result.data.has_more) break;
      if (page === 19) { setRuntimeError("MODIFIER_PRICE_RUNTIME_PAGE_GUARD_REACHED"); setStatus("error"); return; }
    }

    if (fullReconcile) lastFullReconcileAtRef.current = Date.now();

    const latestRuntime = await readCatalogPriceRuntime(identity);
    setRuntime(latestRuntime);
    if (pendingCartWritesRef.current === 0) {
      const readToken = captureCurrentCartRuntimeToken(identity);
      const latestCart = await readLocalCart(identity);
      if (
        pendingCartWritesRef.current === 0
        && isCurrentCartRuntimeTokenCurrent(identity, readToken)
      ) {
        const repairedCart = await reconcileCartCurrencyScale(identity, latestCart, latestRuntime);
        if (pendingCartWritesRef.current === 0) publishDurableCartIfCurrent(repairedCart, readToken);
      }
    }
    setRuntimeError(null);
    setStatus("ready");
  }, [identity, online, publishDurableCartIfCurrent, scopeKey, terminal.deviceSecret]);

  useEffect(() => {
    if (!identity) return;
    let active = true;
    void (async () => {
      const localRuntime = await readCatalogPriceRuntime(identity);
      if (!active) return;
      setRuntime(localRuntime);
      setStatus(localRuntime.menus.length || localRuntime.prices.length ? "local" : "loading");
    })();
    return () => { active = false; };
  }, [identity]);

  useEffect(() => {
    if (!identity) return;
    const subscription = liveQuery(async () => {
      const readToken = captureCurrentCartRuntimeToken(identity);
      const durableCart = await readLocalCart(identity);
      return { durableCart, readToken };
    }).subscribe({
      next: ({ durableCart, readToken }) => {
        if (pendingCartWritesRef.current > 0) return;
        publishDurableCartIfCurrent(durableCart, readToken);
      },
    });
    return () => subscription.unsubscribe();
  }, [identity, publishDurableCartIfCurrent]);

  useRuntimeAutoSync({
    enabled: Boolean(scopeKey && online),
    sync,
    triggerKey: scopeKey,
  });

  const addVariant = useCallback(async (
    menu: LocalCatalogMenu,
    variant: CatalogVariantProjection,
    price: PriceSnapshot,
    selectedModifiers: SelectedModifier[] = [],
  ) => {
    if (!identity) return;
    let currentCart = cartRef.current ?? (await readLocalCart(identity));
    const calculationAtCartStart = currentCart.commerceCalculation
      ?? commerceCalculation
      ?? await readCommerceCalculationRuntime(identity);
    currentCart = cartRef.current ?? currentCart;
    const next = applyLocalCartLine(
      currentCart,
      buildLocalCartLine(menu, variant, price, selectedModifiers),
      currentCart.commerceCalculation ?? calculationAtCartStart,
    );
    publishCart(next);
    enqueueCartSave(next);
  }, [commerceCalculation, enqueueCartSave, identity, publishCart]);

  const addConfiguredVariants = useCallback(async (
    menu: LocalCatalogMenu,
    variant: CatalogVariantProjection,
    price: PriceSnapshot,
    configurations: SelectedModifier[][],
  ) => {
    if (!identity || configurations.length === 0) return;
    let currentCart = cartRef.current ?? (await readLocalCart(identity));
    const calculationAtCartStart = currentCart.commerceCalculation
      ?? commerceCalculation
      ?? await readCommerceCalculationRuntime(identity);
    currentCart = cartRef.current ?? currentCart;
    let next = currentCart;
    for (const selectedModifiers of configurations) {
      next = applyLocalCartLine(
        next,
        buildLocalCartLine(menu, variant, price, selectedModifiers),
        next.commerceCalculation ?? calculationAtCartStart,
      );
    }
    publishCart(next);
    enqueueCartSave(next);
  }, [commerceCalculation, enqueueCartSave, identity, publishCart]);

  const updateConfiguredVariant = useCallback(async (
    lineId: string,
    menu: LocalCatalogMenu,
    variant: CatalogVariantProjection,
    price: PriceSnapshot,
    selectedModifiers: SelectedModifier[],
  ) => {
    if (!identity) return;
    const currentCart = cartRef.current ?? (await readLocalCart(identity));
    const next = applyLocalCartLineConfiguration(
      currentCart,
      lineId,
      buildLocalCartLine(menu, variant, price, selectedModifiers),
    );
    if (next === currentCart) return;
    publishCart(next);
    enqueueCartSave(next);
  }, [enqueueCartSave, identity, publishCart]);

  const updateConfiguredVariants = useCallback(async (
    lineId: string,
    menu: LocalCatalogMenu,
    variant: CatalogVariantProjection,
    price: PriceSnapshot,
    configurations: SelectedModifier[][],
  ) => {
    if (!identity || configurations.length === 0) return;
    const currentCart = cartRef.current ?? (await readLocalCart(identity));
    const next = applyLocalCartLineConfigurations(
      currentCart,
      lineId,
      configurations.map((selectedModifiers) => (
        buildLocalCartLine(menu, variant, price, selectedModifiers)
      )),
    );
    if (next === currentCart) return;
    publishCart(next);
    enqueueCartSave(next);
  }, [enqueueCartSave, identity, publishCart]);

  const changeQuantity = useCallback(async (lineId: string, delta: number) => {
    if (!identity) return;
    const currentCart = cartRef.current ?? (await readLocalCart(identity));
    const next = applyLocalCartLineQuantity(currentCart, lineId, delta);
    publishCart(next);
    enqueueCartSave(next);
  }, [enqueueCartSave, identity, publishCart]);

  return {
    runtime,
    sellableMenus,
    cart,
    status,
    runtimeError,
    reloadLocal,
    sync,
    addVariant,
    addConfiguredVariants,
    updateConfiguredVariant,
    updateConfiguredVariants,
    changeQuantity,
    modifierPriceForOption: (modifierOptionId: string) => modifierPriceIndex.get(modifierOptionId) ?? null,
  };
}
